/**
 * client/src/audio/chapterSeek.ts — "which page holds this point on the chapter axis?"
 *
 * A ±30 s skip near a page edge, a lock-screen scrub, a steering-wheel button: each names a point on
 * §5.7's chapter axis, and the page that holds it may be several pages away — or in the neighbouring
 * chapter. The only answer that does not guess is each page's own `chapterOffsetSec` (the server's
 * §4.6 prefix sums), so this walks pages asking for exactly that, starting from the page we are on.
 *
 * Pure apart from the injected `lookup`, which is how `test/chapter-seek.test.ts` drives it. The walk
 * is linear from the current page rather than a binary search, because the common case is a skip of
 * seconds — one or two lookups — and every lookup is a request on a phone.
 *
 * LAW 3 still holds: the answer is a page and an in-page millisecond, and the caller resolves that
 * millisecond against the page's OWN cue list into `{sid, frac}` before anything seeks.
 */

export interface SeekRow {
  id: string;
  chapterOrd: number;
}

export interface PageAxis {
  /** Milliseconds from the start of the chapter to this page's t=0. */
  offsetMs: number;
  /** The whole chapter, so a target past it can spill into the next one. */
  chapterDurationMs: number;
}

export type AxisLookup = (pageId: string) => Promise<PageAxis | null>;

export interface SeekTarget {
  pageId: string;
  /** Milliseconds into that page's own artifact. */
  inPageMs: number;
}

/**
 * Find the page holding `targetMs` on the chapter axis of `fromPageId`'s chapter.
 *
 * A negative target spills into the END of the previous chapter, a target past the chapter's end
 * into the START of the next — one chapter at most, clamped there, because a skip is seconds and a
 * scrub cannot leave its chapter. Past either end of the book it clamps to the book's edge. `null`
 * when the page is unknown or its own axis cannot be read; the caller then does nothing, which is
 * honest, rather than jumping to a page chosen by arithmetic.
 */
export async function locateChapterMs(
  rows: readonly SeekRow[],
  fromPageId: string,
  targetMs: number,
  lookup: AxisLookup,
): Promise<SeekTarget | null> {
  if (!Number.isFinite(targetMs)) return null;
  const i = rows.findIndex((r) => r.id === fromPageId);
  if (i < 0) return null;
  const ord = rows[i]!.chapterOrd;
  const here = await lookup(fromPageId);
  if (here === null) return null;

  const chapter = rows.filter((r) => r.chapterOrd === ord);
  const at = chapter.findIndex((r) => r.id === fromPageId);

  if (targetMs < 0) {
    const firstIdx = rows.findIndex((r) => r.chapterOrd === ord);
    const prevRow = firstIdx > 0 ? rows[firstIdx - 1] : undefined;
    if (prevRow === undefined) return { pageId: chapter[0]!.id, inPageMs: 0 };
    const prevAxis = await lookup(prevRow.id);
    if (prevAxis === null) return { pageId: chapter[0]!.id, inPageMs: 0 };
    const prev = rows.filter((r) => r.chapterOrd === prevRow.chapterOrd);
    const into = Math.max(0, prevAxis.chapterDurationMs + targetMs);
    return within(prev, prev.length - 1, into, prevAxis.chapterDurationMs, lookup, prevAxis);
  }

  if (targetMs >= here.chapterDurationMs) {
    let lastIdx = -1;
    rows.forEach((r, k) => {
      if (r.chapterOrd === ord) lastIdx = k;
    });
    const nextRow = rows[lastIdx + 1];
    if (nextRow === undefined) {
      // The end of the book. Land a breath before the end of the last page, not on it — exactly at
      // the end is a boundary, and the boundary is end-of-book, which is not what a skip asked for.
      const last = chapter[chapter.length - 1]!;
      const lastAxis = last.id === fromPageId ? here : await lookup(last.id);
      if (lastAxis === null) return null;
      return { pageId: last.id, inPageMs: Math.max(0, here.chapterDurationMs - lastAxis.offsetMs - 1_500) };
    }
    const nextAxis = await lookup(nextRow.id);
    if (nextAxis === null) return { pageId: nextRow.id, inPageMs: 0 };
    const next = rows.filter((r) => r.chapterOrd === nextRow.chapterOrd);
    const into = Math.min(targetMs - here.chapterDurationMs, Math.max(0, nextAxis.chapterDurationMs - 1));
    return within(next, 0, into, nextAxis.chapterDurationMs, lookup, nextAxis);
  }

  return within(chapter, at, targetMs, here.chapterDurationMs, lookup, here);
}

/** The walk inside one chapter, from `start`, whose axis (`startAxis`) is already in hand. */
async function within(
  pages: readonly SeekRow[],
  start: number,
  targetMs: number,
  chapterDurationMs: number,
  lookup: AxisLookup,
  startAxis: PageAxis,
): Promise<SeekTarget> {
  const known = new Map<number, number>([[start, startAxis.offsetMs]]);
  /** The page's offset; the chapter's length for one past the last page; null when unreadable. */
  const offsetOf = async (k: number): Promise<number | null> => {
    if (k >= pages.length) return chapterDurationMs;
    const hit = known.get(k);
    if (hit !== undefined) return hit;
    const axis = await lookup(pages[k]!.id);
    if (axis === null) return null;
    known.set(k, axis.offsetMs);
    return axis.offsetMs;
  };

  let k = start;
  if (targetMs >= startAxis.offsetMs) {
    // Forward while the target is at or past the next page's start.
    while (k + 1 < pages.length) {
      const next = await offsetOf(k + 1);
      if (next === null || targetMs < next) break;
      k++;
    }
  } else {
    // Backward while the target is before this page's start.
    while (k > 0) {
      const cur = await offsetOf(k);
      if (cur === null || targetMs >= cur) break;
      k--;
    }
  }
  const offset = (await offsetOf(k)) ?? 0;
  return { pageId: pages[k]!.id, inPageMs: Math.max(0, targetMs - offset) };
}
